import { useCallback } from 'react'

import { digObject } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import { buildCreateArtworkUrl } from '@hooks/useArtworkBuilderPaths'

import * as artworkActions from '@redux/modules/artwork'
import * as campaignActions from '@redux/modules/campaign'
import * as campaignQuoteItemActions from '@redux/modules/campaignQuoteItem'
import * as orderItemActions from '@redux/modules/orderItem'

import type { AppDispatch } from '@redux/store'

const isEncodedRedirect = (redirect: string) => redirect && redirect !== decodeURIComponent(redirect)

type FetchEditorOptionsParams = {
  artworkId: number,
  dispatch: AppDispatch,
  redirect?: string,
}

const fetchEditorOptions = async (params: FetchEditorOptionsParams) => {
  const { artworkId, dispatch, redirect } = params

  const { loadArtwork: loadArtworkFn } = artworkActions
  const { artworkBuilderConfig: loadFn } = campaignActions

  // Fetch Artwork
  const artworkResult = await dispatch(loadArtworkFn(artworkId))
  // @ts-expect-error TS2339
  if (!artworkResult.success || !artworkResult.data?.campaign_id) return artworkResult

  // @ts-expect-error TS2339
  const editorOptionsResult = await dispatch(loadFn(artworkResult.data?.campaign_id)).then((response) => {
    // @ts-expect-error TS2339
    const { data, errors, success } = response
    if (success){
      const mergedConfig = {
        ...data,
        redirectUrl: redirect,
      }

      return { success: true, data: mergedConfig }
    }

    return { success: false, errors }
  })

  return editorOptionsResult
}

type FetchNewArtworkOptionsParams = {
  campaignQuoteItemId?: number,
  dispatch: AppDispatch,
  orderItemId?: number,
  redirect?: string,
}

const fetchNewArtworkOptions = (params: FetchNewArtworkOptionsParams) => {
  const {
    campaignQuoteItemId, dispatch, orderItemId, redirect,
  } = params

  const { artworkBuilderConfig: campaignQuoteItemFn } = campaignQuoteItemActions
  const { artworkBuilderConfig: orderItemFn } = orderItemActions

  const loadId = campaignQuoteItemId || orderItemId
  const loadFn = campaignQuoteItemId ? campaignQuoteItemFn : orderItemFn

  // @ts-expect-error TS2345
  return dispatch(loadFn(loadId)).then((response) => {
    const { data, errors, success } = response
    if (success){
      const mergedConfig = {
        ...data,
        redirectUrl: redirect,
      }

      return { success: true, data: mergedConfig }
    }

    return { success: false, errors }
  })
}

type DeleteArtworkParams = {
  deleteFn: () => void,
  dispatch: AppDispatch,
  editorContext: any,
  redirect?: string,
}

const deleteArtwork = async (params: DeleteArtworkParams) => {
  const {
    deleteFn, dispatch, editorContext, redirect,
  } = params

  const artwork = digObject(editorContext, 'selectedArtwork', {})

  // Delete Artwork
  const deleteResult = await deleteFn()
  const {
    // @ts-expect-error TS2339
    data: { id: artworkId },
    // @ts-expect-error TS2339
    success: deleteSuccess,
  } = deleteResult

  if (!deleteSuccess) return deleteResult

  // deleteFn comes from Artwork Builder and does not remove the artwork from Redux
  // Remove the deleted Artwork from Redux
  const { removeArtwork: removeArtworkFn } = artworkActions
  dispatch(removeArtworkFn(artworkId))

  // Redirect
  // @ts-expect-error TS2345
  const encodedRedirectUrl = isEncodedRedirect(redirect) ? redirect : encodeURIComponent(redirect)
  const createArtworkUrl = buildCreateArtworkUrl({
    campaignQuoteItem: { id: artwork.campaign_quote_item_id },
    orderItem: { id: artwork.order_item_id },
    redirectUrl: encodedRedirectUrl,
  })

  // @ts-expect-error TS2322
  window.location.href = createArtworkUrl
}

const goBack = (params: { redirect?: string }) => {
  const { redirect } = params

  if (redirect){
    window.location.href = redirect
  }
}

type UseArtworkBuilderOptions = {
  artworkId?: number,
  campaignQuoteItemId?: number,
  orderItemId?: number,
  redirect?: string,
}

function useArtworkBuilder(options: UseArtworkBuilderOptions) {
  const {
    artworkId, campaignQuoteItemId, orderItemId, redirect,
  } = options

  const dispatch = useDispatch()

  const memoFetchEditorOptions = useCallback(() => fetchEditorOptions({
    // @ts-expect-error TS2322
    artworkId,
    dispatch,
    redirect,
  }), [artworkId, redirect])

  return {
    callbacks: {
      deleteArtwork: (deleteFn: () => void, editorContext: any) => deleteArtwork({
        deleteFn, dispatch, editorContext, redirect,
      }),
      fetchEditorOptions: memoFetchEditorOptions,
      fetchNewArtworkOptions: () => fetchNewArtworkOptions({
        campaignQuoteItemId,
        dispatch,
        orderItemId,
        redirect,
      }),
      goBack: () => goBack({ redirect }),
    },
  }
}

export default useArtworkBuilder
